import { AccountBox, AccountCircle, Add, Logout } from '@mui/icons-material';
import {
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box, useMediaQuery, useTheme } from '@mui/system';
import { getSessionUser } from 'api';
import { User } from 'api/models';
import RouterLink from 'components/RouterLink/RouterLink';
import useHasAccess from 'hooks/useHasAccess';
import useRequest from 'hooks/useRequest';
import preval from 'preval.macro';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { dialogState, projectState, usersAtom } from 'state';
import { Permission } from 'state/permissionState';
import { DialogTypes, GeneralIds, RoutePaths } from 'types/types';
import { getRoutePath } from 'utils';

// const VERSION =
//   // @ts-ignore
//   window.env_vars?.REACT_APP_VERSION || process.env.REACT_APP_VERSION;

export default function UserMenu() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [projectStore, setProjectStore] = useRecoilState(projectState);
  const openDialog = useSetRecoilState(dialogState);
  const users = useRecoilValue(usersAtom);
  const canCreateProject = useHasAccess([Permission.SUPERADMIN]);
  const { projectId } = useParams<GeneralIds>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: user } = useRequest<User>(getSessionUser, {
    dependencies: [window.location.pathname, users],
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box ml={1} mt={isMobile ? 1 : 0}>
      <IconButton
        size="small"
        id="fade-button"
        aria-controls={open ? 'fade-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <Tooltip title={t('home.appBar.userMenu')}>
          <AccountCircle fontSize="small" />
        </Tooltip>
      </IconButton>

      <Menu
        id="fade-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{ mt: 1, width: '500px' }}
      >
        <Box pl={2} pr={2} mb={2} display="flex">
          <AccountCircle
            sx={{
              width: '40px',
              height: '40px',
              mt: 1,
            }}
          />
          <Box
            display="flex"
            justifyContent={'center'}
            flexDirection={'column'}
            mt={1}
            ml={1}
          >
            <Typography
              fontWeight="bold"
              variant={'body2'}
              textAlign={'left'}
              fontSize={'17px'}
            >
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant={'body2'} textAlign={'left'}>
              {user?.email}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box
          display="flex"
          justifyContent="center"
          sx={{ mb: 2, mt: 2, width: '300px', pl: 2, pr: 2 }}
        >
          <FormControl fullWidth>
            <InputLabel>{t('general.selectedProject')}</InputLabel>
            <Select
              label={t('general.selectedProject')}
              value={projectId}
              onChange={async e => {
                const selectedProject = projectStore.projects.find(
                  project => project.id === e.target.value,
                );
                setProjectStore({
                  projects: projectStore.projects,
                  selectedProject,
                });
                navigate(
                  getRoutePath(RoutePaths.projectDashboard, {
                    projectId: e.target.value,
                  }),
                );
              }}
              fullWidth
              sx={{
                height: '40px',
              }}
            >
              {projectStore.projects.map(project => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
              <Box
                display={'flex'}
                justifyContent={'center'}
                mt={2}
                mb={1}
                width="100%"
              >
                <Button
                  disabled={!canCreateProject}
                  onClick={() =>
                    openDialog({
                      isOpen: true,
                      dialogType: DialogTypes.addProject,
                    })
                  }
                  startIcon={<Add />}
                  variant="outlined"
                  sx={{ w: '95%' }}
                >
                  {t('projectSelector.addNewProject')}
                </Button>
              </Box>
            </Select>
          </FormControl>
        </Box>
        <Divider />
        <RouterLink to={RoutePaths.profile}>
          <MenuItem onClick={handleClose}>
            <AccountBox sx={{ mr: 1 }} />
            {t('home.menu.profile')}
          </MenuItem>
        </RouterLink>

        <MenuItem
          onClick={() => {
            localStorage.removeItem('token');
            handleClose();
            navigate(RoutePaths.root);
          }}
        >
          <Logout sx={{ mr: 1 }} />
          {t('home.menu.logout')}
        </MenuItem>

        <Typography
          pl={2}
          color={theme => theme.palette.text.secondary}
          fontSize={'small'}
        >
          {t('general.latestBuildTime')}:
          {preval`module.exports = new Date().toLocaleString()`}.
        </Typography>
      </Menu>
    </Box>
  );
}
